import { generateAuthHeaders } from '../utils/auth'
import { handleApiError, ApiError } from '../utils/errorHandler'

const SERVER_HOST = 'https://server.love-study.tech'
const BASE = '/api/v1/wordcloud'

const buildHeaders = (method, path, params = {}) => {
    const token = localStorage.getItem('token')
    return {
        ...generateAuthHeaders(method, path, params),
        ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
}

// 统一检查业务响应，非 200 时通过 errorHandler 弹窗并抛出已处理异常
const checkResp = (json) => {
    if (json.err_code !== 200) {
        handleApiError(json.err_code, json.err_msg)
        throw new ApiError(json.err_code, json.err_msg)
    }
    return json
}

// 统一发送 fetch 并处理错误：HTTP 非 2xx 时先尝试解析 JSON 走 checkResp，解析失败才抛原始文本
const fetchChecked = async (url, options) => {
    const res = await fetch(url, options)
    if (!res.ok) {
        let json
        try { json = await res.json() } catch (_) { /* not json */ }
        if (json && json.err_code !== undefined) {
            checkResp(json) // 会 throw ApiError
        }
        throw new Error(`请求失败 (${res.status})`)
    }
    return res
}

/**
 * 提交词云分析任务（文件上传模式）
 * @param {File} file
 * @returns {Promise<{task_id: string}>}
 */
export const analyzeFile = async (file, vocabulary = []) => {
    const path = `${BASE}/analyze`
    const authHeaders = buildHeaders('POST', path, {})
    // 文件上传时不手动设置 Content-Type，让浏览器自动添加 multipart boundary
    delete authHeaders['Content-Type']

    const formData = new FormData()
    formData.append('file', file)
    if (vocabulary.length > 0) {
        formData.append('vocabulary', JSON.stringify(vocabulary))
    }

    const res = await fetchChecked(SERVER_HOST + path, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
    })
    const json = await res.json()
    checkResp(json)
    return json.data // { task_id }
}

/**
 * 提交词云分析任务（纯文本模式）
 * @param {string} text
 * @returns {Promise<{task_id: string}>}
 */
export const analyzeText = async (text, vocabulary = []) => {
    const path = `${BASE}/analyze`
    const authHeaders = buildHeaders('POST', path, {})

    const body = { text }
    if (vocabulary.length > 0) body.vocabulary = vocabulary

    const res = await fetchChecked(SERVER_HOST + path, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(body),
    })
    const json = await res.json()
    checkResp(json)
    return json.data // { task_id }
}

/**
 * 订阅 SSE 进度流
 * @param {string} taskId
 * @param {(data:{current,total,message})=>void} onProgress
 * @param {(data:Array)=>void} onResult  - data 是 [[word, count], ...]
 * @param {(data:{message:string})=>void} onError
 * @returns {Promise<void>}
 */
export const subscribeStream = async (taskId, onProgress, onResult, onError, onStats = () => {}) => {
    const path = `/api/v1/wordcloud/stream/${taskId}`
    const authHeaders = buildHeaders('GET', path, {})
    delete authHeaders['Content-Type']

    const response = await fetchChecked(SERVER_HOST + path, { headers: authHeaders })

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        // SSE 事件以 \n\n 分隔
        const parts = buffer.split('\n\n')
        buffer = parts.pop() // 保留未完成的尾部片段

        for (const part of parts) {
            if (!part.trim()) continue
            const lines = part.split('\n')
            let eventType = null
            let eventData = null
            for (const line of lines) {
                if (line.startsWith('event: ')) eventType = line.slice(7).trim()
                else if (line.startsWith('data: ')) eventData = line.slice(6).trim()
            }
            if (!eventType || !eventData) continue
            try {
                const parsed = JSON.parse(eventData)
                if (eventType === 'progress') onProgress(parsed)
                else if (eventType === 'result') { reader.cancel(); onResult(parsed); return }
                else if (eventType === 'error') { reader.cancel(); onError(parsed); return }
                else if (eventType === 'stats') onStats(parsed)
            } catch (e) {
                console.error('[SSE] 解析失败:', e, eventData)
            }
        }
    }
}

/**
 * 提交词云图生成任务
 * @param {{ data: Array<[string, number]>, shape: string }} payload
 * @returns {Promise<{task_id: string}>}
 */
export const generateWordCloud = async (payload) => {
    const path = `${BASE}/generate`
    const authHeaders = buildHeaders('POST', path, {})

    const res = await fetchChecked(SERVER_HOST + path, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
    })
    const json = await res.json()
    return json // { task_id, cached }
}

/**
 * 订阅词云图生成 SSE 流
 * @param {string} taskId
 * @param {(data:{current,total,message})=>void} onProgress
 * @param {(data:{width,height,word_count,shape,image:string})=>void} onResult
 * @param {(data:{message:string})=>void} onError
 * @returns {Promise<void>}
 */
/**
 * 获取关键词（文件上传模式）
 * @param {File} file
 * @param {number} topN
 * @returns {Promise<Array<{word: string, score: number}>>}
 */
export const fetchKeywordsFile = async (file, topN = 50) => {
    const path = `${BASE}/keywords`
    const authHeaders = buildHeaders('POST', path, {})
    delete authHeaders['Content-Type']

    const formData = new FormData()
    formData.append('file', file)
    formData.append('num', String(topN))

    const res = await fetchChecked(SERVER_HOST + path, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
    })
    const json = await res.json()
    checkResp(json)
    return json.data // [{word, score}, ...]
}

/**
 * 获取关键词（文本模式）— 将文本转为 txt blob 后发送
 * @param {string} text
 * @param {number} topN
 * @returns {Promise<Array<{word: string, score: number}>>}
 */
export const fetchKeywordsText = async (text, topN = 50) => {
    const path = `${BASE}/keywords`
    const authHeaders = buildHeaders('POST', path, {})
    delete authHeaders['Content-Type']

    const blob = new Blob([text], { type: 'text/plain' })
    const formData = new FormData()
    formData.append('file', blob, 'input.txt')
    formData.append('num', String(topN))

    const res = await fetchChecked(SERVER_HOST + path, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
    })
    const json = await res.json()
    checkResp(json)
    return json.data
}

/**
 * 提交词语关系网络图分析任务
 * @param {File|null} file  - 文件（文件模式时传入）
 * @param {string|null} textContent - 文本内容（文本模式时传入）
 * @param {Array<{word: string, score: number}>} keywords
 * @param {number} topN
 * @param {string} mode
 * @returns {Promise<{task_id: string}>}
 */
export const analyzeNetwork = async (file, textContent, keywords = [], topN = 50, mode = 'pmi') => {
    const path = `${BASE}/analyze/network`
    const authHeaders = buildHeaders('POST', path, {})
    delete authHeaders['Content-Type']

    const formData = new FormData()
    if (file) {
        formData.append('file', file)
    } else if (textContent) {
        const blob = new Blob([textContent], { type: 'text/plain' })
        formData.append('file', blob, 'input.txt')
    }
    formData.append('top_n', String(topN))
    formData.append('mode', mode)
    formData.append('keywords', JSON.stringify(keywords))

    const res = await fetchChecked(SERVER_HOST + path, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
    })
    const json = await res.json()
    checkResp(json)
    return json.data // { task_id }
}

/**
 * 订阅词语关系网络图 SSE 流
 * @param {string} taskId
 * @param {(data:{current,total,message})=>void} onProgress
 * @param {(data:{nodes,edges,mode})=>void} onResult
 * @param {(data:{message:string})=>void} onError
 */
export const subscribeNetworkStream = async (taskId, onProgress, onResult, onError) => {
    const path = `/api/v1/wordcloud/stream/${taskId}`
    const authHeaders = buildHeaders('GET', path, {})
    delete authHeaders['Content-Type']

    const response = await fetchChecked(SERVER_HOST + path, { headers: authHeaders })

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        const parts = buffer.split('\n\n')
        buffer = parts.pop()

        for (const part of parts) {
            if (!part.trim()) continue
            const lines = part.split('\n')
            let eventType = null
            let eventData = null
            for (const line of lines) {
                if (line.startsWith('event: ')) eventType = line.slice(7).trim()
                else if (line.startsWith('data: ')) eventData = line.slice(6).trim()
            }
            if (!eventType || !eventData) continue
            try {
                const parsed = JSON.parse(eventData)
                if (eventType === 'progress') onProgress(parsed)
                else if (eventType === 'result') { reader.cancel(); onResult(parsed); return }
                else if (eventType === 'error') { reader.cancel(); onError(parsed); return }
            } catch (e) {
                console.error('[SSE] 解析失败:', e, eventData)
            }
        }
    }
}

/**
 * 提交评论分析任务（文件上传模式）
 * @param {File} file
 * @returns {Promise<{task_id: string}>}
 */
export const analyzeSentimentFile = async (file) => {
    const path = `${BASE}/sentiment`
    const authHeaders = buildHeaders('POST', path, {})
    delete authHeaders['Content-Type']

    const formData = new FormData()
    formData.append('file', file)

    const res = await fetchChecked(SERVER_HOST + path, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
    })
    const json = await res.json()
    checkResp(json)
    return json.data // { task_id }
}

/**
 * 提交评论分析任务（纯文本模式）
 * @param {string} text
 * @returns {Promise<{task_id: string}>}
 */
export const analyzeSentimentText = async (text) => {
    const path = `${BASE}/sentiment`
    const authHeaders = buildHeaders('POST', path, {})
    delete authHeaders['Content-Type']

    const blob = new Blob([text], { type: 'text/plain' })
    const formData = new FormData()
    formData.append('file', blob, 'input.txt')

    const res = await fetchChecked(SERVER_HOST + path, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
    })
    const json = await res.json()
    checkResp(json)
    return json.data // { task_id }
}

/**
 * 提交情感词云（comment_tag）分析任务
 * @param {File} file
 * @param {number} type - 行业类型（1-13）
 * @returns {Promise<{task_id: string}>}
 */
export const analyzeCommentTag = async (file, type = 4) => {
    const path = `${BASE}/comment_tag`
    const authHeaders = buildHeaders('POST', path, {})
    delete authHeaders['Content-Type']

    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', String(type))

    const res = await fetchChecked(SERVER_HOST + path, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
    })
    const json = await res.json()
    checkResp(json)
    return json.data // { task_id }
}

/**
 * 提交情感词云（comment_tag）分析任务（纯文本模式）
 * @param {string} text
 * @param {number} type - 行业类型（1-13）
 * @returns {Promise<{task_id: string}>}
 */
export const analyzeCommentTagText = async (text, type = 4) => {
    const path = `${BASE}/comment_tag`
    const authHeaders = buildHeaders('POST', path, {})
    delete authHeaders['Content-Type']

    const blob = new Blob([text], { type: 'text/plain' })
    const formData = new FormData()
    formData.append('file', blob, 'input.txt')
    formData.append('type', String(type))

    const res = await fetchChecked(SERVER_HOST + path, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
    })
    const json = await res.json()
    checkResp(json)
    return json.data // { task_id }
}

export const subscribeGenerateStream = async (taskId, onProgress, onResult, onError) => {
    const path = `/api/v1/wordcloud/stream/generate/${taskId}`
    const authHeaders = buildHeaders('GET', path, {})
    delete authHeaders['Content-Type']

    const response = await fetchChecked(SERVER_HOST + path, { headers: authHeaders })

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        const parts = buffer.split('\n\n')
        buffer = parts.pop()

        for (const part of parts) {
            if (!part.trim()) continue
            const lines = part.split('\n')
            let eventType = null
            let eventData = null
            for (const line of lines) {
                if (line.startsWith('event: ')) eventType = line.slice(7).trim()
                else if (line.startsWith('data: ')) eventData = line.slice(6).trim()
            }
            if (!eventType || !eventData) continue
            try {
                const parsed = JSON.parse(eventData)
                if (eventType === 'progress') onProgress(parsed)
                else if (eventType === 'result') { reader.cancel(); onResult(parsed); return }
                else if (eventType === 'error') { reader.cancel(); onError(parsed); return }
            } catch (e) {
                console.error('[SSE] 解析失败:', e, eventData)
            }
        }
    }
}
